import { getAuthRedirectQueryValue } from '@/hooks/use-auth-redirect';
import { inviteCodeQueryString } from '@/providers/invite-code';
import {
    EnumFeature,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
import { logger } from '@rhg/libs/logger';
import { ftueQueryString } from '@rhg/utils/constants';
import { getSingleStringFromQuery, routes } from '@rhg/utils/routes';
import { Router } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { BaseProperties } from '../base-context-provider';
import { AnalyticsDestination } from '../destination/types';
import mapBaseEventProps from '../map-base-event-props';
import { analyticsPathnameMap } from './types';

interface TrackAsPageNavProps {
    screenName: EnumScreenName;
    feature: EnumFeature;
    destinations: AnalyticsDestination[];
    query?: ParsedUrlQuery;
    additionalProps?: EventProperties;
    baseProperties: BaseProperties;
}

interface TrackPageNavProps {
    pathname: Router['pathname'];
    query: Router['query'];
    destinations: AnalyticsDestination[];
    baseProperties: BaseProperties;
}

/** Registration screens determine this on their own via the current RegistrationFlow */
const preRegistrationScreenNameWithIsDependent: EnumScreenName[] = [
    EnumScreenName.Landing,
    EnumScreenName.LoginRegistration,
    EnumScreenName.Signup,
];

/**
 * Returns a mapping of the desired analytics properties provided by
 * the page's query params.
 * @param query The URL's query map
 */
const getQueryAdditionalAnalyticsProperties = (
    screenName: EnumScreenName,
    query?: ParsedUrlQuery
): EventProperties => {
    const onboardingQuery =
        query &&
        (query[ftueQueryString] ||
            getAuthRedirectQueryValue(query, ftueQueryString));
    const inviteQuery =
        query &&
        (query[inviteCodeQueryString] ||
            getAuthRedirectQueryValue(query, inviteCodeQueryString));
    const searchQueryString =
        getSingleStringFromQuery(query ?? {}, 'query') ?? '';
    const searchCategories =
        getSingleStringFromQuery(query ?? {}, 'categories')?.split(',') ?? [];
    const searchDurations =
        getSingleStringFromQuery(query ?? {}, 'durations')?.split(',') ?? [];

    return {
        ...(onboardingQuery
            ? { IsOnboarding: onboardingQuery === 'true' }
            : {}),
        ...(preRegistrationScreenNameWithIsDependent.includes(screenName)
            ? { IsDependent: Boolean(inviteQuery) }
            : {}),
        ...(screenName === EnumScreenName.Search
            ? {
                  FilterCategory: searchCategories,
                  FilterDuration: searchDurations,
                  QueryCharacterCount: searchQueryString.length,
              }
            : {}),
    };
};

/**
 * Returns the analytics values for a given route
 * @param url
 * @returns [EnumScreenName, EnumFeature]
 * @example
 * getAnalyticsForRouteAndQuery('/screening') // [EnumScreenName.FtueSurveyStart, EnumFeature.FtueSurvey]
 **/
function getAnalyticsForRoute({
    pathname,
    query,
    baseProperties,
}: {
    pathname: Router['pathname'];
    query: Router['query'];
    baseProperties?: Partial<Pick<BaseProperties, 'IsConcierge'>>;
}): [EnumScreenName, EnumFeature] {
    if (baseProperties?.IsConcierge && pathname === routes.careplan) {
        return [EnumScreenName.ConciergeCarePlan, EnumFeature.Concierge];
    }
    // Handle search route, which is just query params on routes.discover
    if (pathname === routes.discover) {
        if (typeof query['query'] !== 'undefined') {
            return (
                analyticsPathnameMap[routes.search] ?? [
                    EnumScreenName.NotMapped,
                    EnumFeature.NotMapped,
                ]
            );
        }
    }

    return (
        analyticsPathnameMap[pathname] ?? [
            EnumScreenName.NotMapped,
            EnumFeature.NotMapped,
        ]
    );
}

export function getScreenName(
    {
        pathname,
        query,
    }: { pathname: Router['pathname']; query: Router['query'] },
    baseProperties: Pick<BaseProperties, 'IsAuthenticated'> &
        Partial<Pick<BaseProperties, 'IsConcierge'>>
) {
    if (baseProperties.IsAuthenticated && pathname === routes.dashboard) {
        return EnumScreenName.Home;
    }

    return getAnalyticsForRoute({ pathname, query, baseProperties })?.[0];
}

export function getFeatureName(
    {
        pathname,
        query,
    }: {
        pathname: Router['pathname'];
        query: Router['query'];
    },
    baseProperties?: Partial<Pick<BaseProperties, 'IsConcierge'>>
) {
    return getAnalyticsForRoute({
        pathname,
        query,
        baseProperties,
    })?.[1];
}

/**
 * Tracks a page nav event that isn't tied to a specific route
 * @param screenName
 */
export async function trackAsPageNav({
    screenName,
    feature,
    query,
    destinations,
    additionalProps,
    baseProperties,
}: TrackAsPageNavProps) {
    const properties = {
        ...additionalProps,
        IsScreenView: true,
        ScreenName: screenName,
        Feature: feature,
    };

    await Promise.all(
        destinations.map((dest) =>
            dest.track(
                'Screen : Viewed',
                mapBaseEventProps({
                    ...baseProperties,
                    ...getQueryAdditionalAnalyticsProperties(screenName, query),
                    ...properties,
                })
            )
        )
    );
}

export default async function trackPageNav({
    pathname,
    query,
    destinations,
    baseProperties,
}: TrackPageNavProps) {
    // The auth route is a transient redirection point
    // don't log it as its own page view
    if (pathname === routes.auth) {
        return;
    }

    const screenName = getScreenName({ pathname, query }, baseProperties);
    const featureName = getFeatureName({ pathname, query }, baseProperties);

    if (screenName === EnumScreenName.NotMapped) {
        logger.warn('Missing event name for path', { path: pathname });
        await Promise.all(
            destinations.map((dest) =>
                dest.track(
                    'UnmappedPageNav',
                    mapBaseEventProps({ ...baseProperties })
                )
            )
        );
        return;
    }

    const properties = {
        IsScreenView: true,
        ScreenName: screenName,
        Feature: featureName ?? EnumFeature.NotMapped,
    };

    await Promise.all(
        destinations.map((dest) =>
            dest.track(
                'Screen : Viewed',
                mapBaseEventProps({
                    ...baseProperties,
                    ...getQueryAdditionalAnalyticsProperties(screenName, query),
                    ...properties,
                })
            )
        )
    );
}
